import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AVATAR_OPTIONS = [12, 25, 33, 45, 51, 60].map((n) => `https://i.pravatar.cc/300?img=${n}`);

export default function Onboarding() {
  const [form, setForm] = useState({ name: "", username: "", gender: "", dob: "", avatar: AVATAR_OPTIONS[0] });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/onboard", form);
      setUser(data.user);
      toast.success("Welcome to Yaara Live! 🎉 +500 bonus coins");
      navigate("/");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen max-w-md mx-auto px-6 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-extrabold font-display mb-1">Set up your profile</h1>
        <p className="text-zinc-400 text-sm mb-8">Let people know who's in the room ✨</p>

        <p className="text-xs font-semibold text-zinc-400 mb-2">Pick an avatar</p>
        <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar pb-1">
          {AVATAR_OPTIONS.map((a) => (
            <button key={a} data-testid={`avatar-option-${a.slice(-2)}`} onClick={() => set("avatar", a)}
              className={`shrink-0 rounded-full p-0.5 ${form.avatar === a ? "gradient-primary" : "bg-white/10"}`}>
              <img src={a} alt="" className="w-14 h-14 rounded-full border-2 border-white" />
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <input data-testid="onboard-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder="Display name"
            className="w-full bg-[#131A2A] border border-white/10 rounded-2xl px-4 py-3.5 outline-none text-white text-sm focus:border-violet-500 transition-colors placeholder:text-zinc-500" />
          <input data-testid="onboard-username-input" value={form.username} onChange={(e) => set("username", e.target.value)}
            placeholder="@username"
            className="w-full bg-[#131A2A] border border-white/10 rounded-2xl px-4 py-3.5 outline-none text-white text-sm focus:border-violet-500 transition-colors placeholder:text-zinc-500" />
          <div className="flex gap-2">
            {["male", "female", "other"].map((g) => (
              <button key={g} data-testid={`gender-${g}`} onClick={() => set("gender", g)}
                className={`flex-1 py-3 rounded-2xl text-sm font-semibold capitalize transition-colors ${form.gender === g ? "gradient-primary text-white" : "bg-[#131A2A] text-zinc-400 border border-white/10"}`}>
                {g}
              </button>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-400 block mb-1.5">Date of birth (18+ required)</label>
            <input data-testid="onboard-dob-input" type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)}
              className="w-full bg-[#131A2A] border border-white/10 rounded-2xl px-4 py-3.5 outline-none text-white text-sm focus:border-violet-500 transition-colors [color-scheme:dark]" />
          </div>
          <button data-testid="onboard-submit-button" onClick={submit}
            disabled={!form.name || form.username.length < 3 || !form.dob || busy}
            className="w-full gradient-primary text-white font-bold py-3.5 rounded-2xl disabled:opacity-40 active:scale-[0.98] transition-transform">
            {busy ? "Creating…" : "Enter Yaara Live 🚀"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
